// client/src/pages/OperatorsDashboard/Reward.tsx
import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Zap, TrendingUp, Trophy, Flame, CheckCircle, XCircle, Info } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Area, AreaChart
} from "recharts";
import { useApp } from "@/contexts/AppContext";
import { Helmet } from "react-helmet-async";
import { kxtService } from "@/api/services/kxtService";
import { tokenService } from "@/api/services/tokenService";
import { useTranslation } from "react-i18next";
import { KXT_REDEMPTION_ENABLED } from "@/lib/featureFlags";

export default function DashboardReward() {
  const { theme } = useApp();
  const { t } = useTranslation();

  // Data states
  const [kxtBalance, setKxtBalance] = useState<number>(0);
  const [pendingRewards, setPendingRewards] = useState<number>(0);
  interface RewardScoreData {
    rewardScore?: number;
    rank?: number;
    metrics?: Record<string, { weight?: string; normalizedScore?: number }>;
    message?: string;
  }
  const [rewardScore, setRewardScore] = useState<RewardScoreData | null>(null);

  interface StreakData {
    currentStreak?: number;
    longestStreak?: number;
    streakBonus?: string;
    nextMilestone?: { daysLeft?: number; bonus?: number; days?: number };
  }
  const [streak, setStreak] = useState<StreakData | null>(null);

  interface LeaderboardEntry {
    operatorId?: string;
    userId?: string;
    operator?: { id?: string; name?: string };
    rank?: number;
    name?: string;
    totalReward?: number;
    totalRewardKXT?: number;
    finalRewardKXT?: number;
  }
  interface LeaderboardData {
    leaderboard?: LeaderboardEntry[];
    myRank?: number;
    myScore?: number;
    myReward?: number;
  }
  const [leaderboard, setLeaderboard] = useState<LeaderboardData | null>(null);
  const [earningsHistory, setEarningsHistory] = useState<Record<string, unknown>[]>([]);
  const [monthlyData, setMonthlyData] = useState<Array<{ month: string; amount: number }>>([]);

  // UI states
  const [loading, setLoading] = useState(true);
  const [claimLoading, setClaimLoading] = useState(false);
  const [isLeaderboardLoading, setIsLeaderboardLoading] = useState(false);
  const [leaderboardPeriod, setLeaderboardPeriod] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  // ── Fetch all data ────────────────────────────────────────────────────
  const fetchAll = async () => {
    setLoading(true);
    try {
      const [balanceData, tokenSummary, rewardData, streakData, earningsData] = await Promise.allSettled([
        kxtService.getBalance(),
        tokenService.getTokenSummary(),
        kxtService.getRewardScore(),
        kxtService.getStreak(),
        kxtService.getEarningsHistory(1, 20),
      ]);

      // Balance
      if (balanceData.status === 'fulfilled') {
        setKxtBalance(balanceData.value.kxtBalance || 0);
      }

      // Pending rewards (from token summary)
      if (tokenSummary.status === 'fulfilled') {
        setPendingRewards(Number((tokenSummary.value as { pendingRewards?: number }).pendingRewards) || 0);
      }

      // Reward score
      if (rewardData.status === 'fulfilled') {
        setRewardScore(rewardData.value);
      }

      // Streak
      if (streakData.status === 'fulfilled') {
        setStreak(streakData.value as unknown as StreakData);
      }

      // Earnings history -> monthly chart
      if (earningsData.status === 'fulfilled') {
        const txns = (earningsData.value as unknown as { transactions?: Record<string, unknown>[] }).transactions || [];
        setEarningsHistory(txns);

        // Group by month+year to avoid Jan-2025 colliding with Jan-2026.
        // Only count positive amounts (earnings); withdrawals should not inflate the chart.
        const grouped: Record<string, { label: string; year: number; monthIndex: number; amount: number }> = {};
        txns.forEach((tx: { amount?: string | number; createdAt: string | number; [key: string]: unknown }) => {
          const amount = Number(tx.amount) || 0;
          if (amount <= 0) return; // skip withdrawals / negative entries
          const d = new Date(tx.createdAt);
          if (isNaN(d.getTime())) return;
          const monthLabel = d.toLocaleString('default', { month: 'short' });
          const year = d.getFullYear();
          const key = `${year}-${d.getMonth()}`;
          if (!grouped[key]) {
            grouped[key] = { label: `${monthLabel} ${year}`, year, monthIndex: d.getMonth(), amount: 0 };
          }
          grouped[key].amount += amount;
        });
        // Sort chronologically so the chart reads left-to-right oldest-to-newest
        const sorted = Object.values(grouped).sort((a, b) =>
          a.year !== b.year ? a.year - b.year : a.monthIndex - b.monthIndex
        );
        setMonthlyData(sorted.map(({ label, amount }) => ({ month: label, amount })));
      }
    } catch (err: unknown) {
      console.error('Error fetching reward data:', err);
    } finally {
      setLoading(false);
    }
  };

  // ── Fetch leaderboard separately (changes with period) ───────────────
  const fetchLeaderboard = async () => {
    setIsLeaderboardLoading(true);
    try {
      const data = await kxtService.getLeaderboard(leaderboardPeriod);
      setLeaderboard(data as unknown as LeaderboardData);
    } catch (err: unknown) {
      console.error('Error fetching leaderboard:', err);
    } finally {
      setIsLeaderboardLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { fetchLeaderboard(); }, [leaderboardPeriod]);

  // ── Claim rewards ────────────────────────────────────────────────────
  const handleClaimRewards = async () => {
    setClaimLoading(true);
    try {
      const result = await tokenService.claimTokens();
      if (result.success) {
        showMessage('success', result.message || 'Rewards claimed successfully!');
        await fetchAll();
      } else {
        showMessage('error', result.message || 'Failed to claim rewards');
      }
    } catch (err: unknown) {
      const errorObj = err as {response?: {data?: {message?: string}}};
      showMessage('error', errorObj.response?.data?.message || 'Failed to claim rewards');
    } finally {
      setClaimLoading(false);
    }
  };

  // ── Streak color helper ───────────────────────────────────────────────
  const getStreakColor = (days: number) => {
    if (days >= 10) return 'text-yellow-500';
    if (days >= 5) return 'text-orange-500';
    return 'text-blue-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t("operator.reward.meta.title", { defaultValue: "Rewards Dashboard | KonectX" })}</title>
        <meta
          name="description"
          content={t("operator.reward.meta.description", {
            defaultValue: "View and manage your KXT rewards on KonectX.",
          })}
        />
      </Helmet>

      <div className="space-y-6 p-6 sm:p-7">
        {/* Message */}
        {message && (
          <div className={`p-4 rounded-lg flex items-center gap-2 text-sm ${
            message.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/20 text-green-600'
              : 'bg-red-50 dark:bg-red-900/20 text-red-600'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="w-4 h-4 flex-shrink-0" />
            ) : (
              <XCircle className="w-4 h-4 flex-shrink-0" />
            )}
            <span>{message.text}</span>
          </div>
        )}

        {/* ── Stats Cards ─────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* KXT Balance */}
          <Card className={`p-6 ${theme === 'dark' ? 'bg-[#333436] border-[#2b2b2c]' : 'bg-white border-gray-200'}`}>
            <p className={`text-sm mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              {t("operator.reward.cards.kxtBalance", { defaultValue: "KXT Balance" })}
            </p>
            <h3 className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              {kxtBalance.toFixed(2)}
            </h3>
            <p className="text-xs mt-1 text-gray-400">
              {t("operator.reward.cards.walletBalance", { defaultValue: "Wallet Balance" })}
            </p>
          </Card>

          {/* Pending Rewards */}
          <Card className={`p-6 ${theme === 'dark' ? 'bg-[#333436] border-[#2b2b2c]' : 'bg-white border-gray-200'}`}>
            <p className={`text-sm mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              {t("operator.reward.cards.pendingRewards", { defaultValue: "Pending Rewards" })}
            </p>
            <h3 className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              {pendingRewards.toFixed(2)}
            </h3>
            {/* Claim is a KXT redemption control — gated OFF for the beta (counsel,
                KX-LEG-KXT-001 Q1–Q2; KX-111 item 9). Pending rewards remain visible
                as accrued rewards (read-only). */}
            {KXT_REDEMPTION_ENABLED && (
              <p className="text-xs mt-2">
                <Button
                  onClick={handleClaimRewards}
                  disabled={claimLoading || pendingRewards <= 0}
                  className="px-3 py-1 text-xs bg-[#2E5A88] hover:bg-[#244a73] text-white disabled:opacity-50"
                >
                  {claimLoading ? (
                    <>
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      {t("operator.reward.cards.claiming", { defaultValue: "Claiming..." })}
                    </>
                  ) : (
                    t("operator.reward.cards.claim", { defaultValue: "Claim" })
                  )}
                </Button>
              </p>
            )}
          </Card>

          {/* Reward Score */}
          <Card className={`p-6 ${theme === 'dark' ? 'bg-[#333436] border-[#2b2b2c]' : 'bg-white border-gray-200'}`}>
            <p className={`text-sm mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              {t("operator.reward.cards.rewardScore", { defaultValue: "Reward Score" })}
            </p>
            <h3 className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              {rewardScore?.rewardScore?.toFixed(1) || '0.0'}
            </h3>
            <p className="text-xs mt-1 text-gray-400">
              {t("operator.reward.cards.rank", { defaultValue: "Rank" })} #{rewardScore?.rank || t("common.na", { defaultValue: "N/A" })}
            </p>
          </Card>

          {/* Streak */}
          <Card className={`p-6 ${theme === 'dark' ? 'bg-[#333436] border-[#2b2b2c]' : 'bg-white border-gray-200'}`}>
            <p className={`text-sm mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              {t("operator.reward.cards.currentStreak", { defaultValue: "Current Streak" })}
            </p>
            <h3 className={`text-3xl font-bold flex items-center gap-2 ${getStreakColor(streak?.currentStreak || 0)}`}>
              <Flame className="w-7 h-7" />
              {streak?.currentStreak || 0} {t("operator.reward.cards.days", { defaultValue: "days" })}
            </h3>
            <p className="text-xs mt-1 text-gray-400">
              {t("operator.reward.cards.bonus", { defaultValue: "Bonus" })}: {streak?.streakBonus || '0%'}
            </p>
          </Card>
        </div>

        {/* ── Reward Score Breakdown + Streak Progress ─────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Reward Score Breakdown */}
          <Card className={`p-6 ${theme === 'dark' ? 'bg-[#333436] border-[#2b2b2c]' : 'bg-white border-gray-200'}`}>
            <h3 className={`text-lg font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              <TrendingUp className="inline w-5 h-5 mr-2" />
              {t("operator.reward.breakdown.title", { defaultValue: "Reward Score Breakdown" })}
            </h3>
            {rewardScore?.metrics ? (
              <div className="space-y-3">
                {Object.entries(rewardScore.metrics).map(([key, val]) => (
                  <div key={key}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className={`capitalize ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                        {key.replace(/([A-Z])/g, ' $1')} <span className="text-gray-400">({val.weight})</span>
                      </span>
                      <span className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {((val.normalizedScore || 0) * 100).toFixed(0)}%
                      </span>
                    </div>
                    <div className={`w-full rounded-full h-2 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`}>
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all"
                        style={{ width: `${(val.normalizedScore || 0) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                {rewardScore?.message ||
                  t("operator.reward.breakdown.empty", {
                    defaultValue: "No metrics yet. Start serving users to earn rewards.",
                  })}
              </p>
            )}
          </Card>

          {/* Streak Info */}
          <Card className={`p-6 ${theme === 'dark' ? 'bg-[#333436] border-[#2b2b2c]' : 'bg-white border-gray-200'}`}>
            <h3 className={`text-lg font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              <Flame className="inline w-5 h-5 mr-2 text-orange-500" />
              {t("operator.reward.streak.title", { defaultValue: "Streak Progress" })}
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t("operator.reward.streak.current", { defaultValue: "Current Streak" })}
                </span>
                <span className={`font-bold text-orange-500`}>
                  {streak?.currentStreak || 0} {t("operator.reward.cards.days", { defaultValue: "days" })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t("operator.reward.streak.longest", { defaultValue: "Longest Streak" })}
                </span>
                <span className={`font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {streak?.longestStreak || 0} {t("operator.reward.cards.days", { defaultValue: "days" })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t("operator.reward.streak.bonus", { defaultValue: "Streak Bonus" })}
                </span>
                <span className="font-bold text-yellow-500">{streak?.streakBonus || '0%'}</span>
              </div>
              {streak?.nextMilestone && (
                <div className={`mt-4 p-3 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-blue-50'}`}>
                  <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-blue-600'}`}>
                    🎯 {t("operator.reward.streak.milestone", {
                      defaultValue: "{{days}} more days to unlock {{bonus}} bonus!",
                      days: streak.nextMilestone.daysLeft,
                      bonus: streak.nextMilestone.bonus,
                    })}
                  </p>
                  <div className={`w-full rounded-full h-2 mt-2 ${theme === 'dark' ? 'bg-gray-700' : 'bg-blue-200'}`}>
                    <div
                      className="bg-blue-500 h-2 rounded-full transition-all"
                      style={{
                        width: `${Math.min(100, Math.max(0, ((streak.currentStreak || 0) / (streak.nextMilestone.days || 1)) * 100))}%`
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* ── Monthly Earnings Chart ───────────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className={`p-6 ${theme === 'dark' ? 'bg-[#333436] border-[#2b2b2c]' : 'bg-white border-gray-200'}`}>
            <h3 className={`text-lg font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              {t("operator.reward.chart.title", { defaultValue: "Monthly Earnings" })}
            </h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#374151' : '#e5e7eb'} />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: theme === 'dark' ? '#9ca3af' : '#6b7280', fontSize: 12 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: theme === 'dark' ? '#9ca3af' : '#6b7280', fontSize: 12 }} />
                <Tooltip contentStyle={{ backgroundColor: theme === 'dark' ? '#1f2937' : '#fff', border: '1px solid #374151', color: theme === 'dark' ? '#fff' : '#000' }} />
                <Bar dataKey="amount" fill="#4f46e5" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          {/* Leaderboard */}
          <Card className={`p-6 ${theme === 'dark' ? 'bg-[#333436] border-[#2b2b2c]' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-semibold flex items-center gap-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                <Trophy className="w-5 h-5 text-yellow-500" />
                {t("operator.reward.leaderboard.title", { defaultValue: "Leaderboard" })}
                {isLeaderboardLoading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
              </h3>
              <select
                value={leaderboardPeriod}
                onChange={(e) => setLeaderboardPeriod(e.target.value as 'daily' | 'weekly' | 'monthly')}
                disabled={isLeaderboardLoading}
                className={`text-sm border rounded px-2 py-1 disabled:opacity-50 ${theme === 'dark' ? 'bg-gray-800 border-gray-700 text-white' : 'border-gray-300'}`}
              >
                <option value="daily">{t("operator.reward.leaderboard.daily", { defaultValue: "Daily" })}</option>
                <option value="weekly">{t("operator.reward.leaderboard.weekly", { defaultValue: "Weekly" })}</option>
                <option value="monthly">{t("operator.reward.leaderboard.monthly", { defaultValue: "Monthly" })}</option>
              </select>
            </div>

            {leaderboard?.myRank != null && (
              <div className={`mb-3 p-3 rounded-lg text-sm font-medium ${theme === 'dark' ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-50 text-blue-700'}`}>
                {t("operator.reward.leaderboard.yourRank", { defaultValue: "Your Rank" })}: #{leaderboard.myRank} ·{" "}
                {t("operator.reward.leaderboard.score", { defaultValue: "Score" })}: {leaderboard.myScore?.toFixed(1) ?? "0.0"} ·{" "}
                {t("operator.reward.leaderboard.reward", { defaultValue: "Reward" })}: {leaderboard.myReward?.toFixed(2) ?? "0.00"} KXT
              </div>
            )}

            {(leaderboard?.leaderboard && leaderboard.leaderboard.length > 0) ? (
              <div className="space-y-2 overflow-y-auto max-h-48">
                {leaderboard.leaderboard.map((entry, idx) => {
                  const key = entry.operatorId || entry.userId || entry.operator?.id || `lb-${entry.rank ?? idx}`;
                  return (
                    <div key={key} className={`flex items-center justify-between p-2 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}`}>
                      <div className="flex items-center gap-3">
                        <span className={`text-sm font-bold w-6 ${idx === 0 ? 'text-yellow-500' : idx === 1 ? 'text-gray-400' : idx === 2 ? 'text-orange-400' : theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          #{entry.rank || idx + 1}
                        </span>
                        <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          {entry.operator?.name || t("operator.reward.leaderboard.unknownOperator", { defaultValue: "Operator" })}
                        </span>
                      </div>
                      <span className={`text-sm font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {entry.finalRewardKXT?.toFixed(2) ?? "0.00"} KXT
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className={`text-sm text-center py-8 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                {t("operator.reward.leaderboard.empty", { defaultValue: "No leaderboard data yet" })}
              </p>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}